import React, { useState, useEffect, useMemo } from "react";

import {
  Building2,
  Users,
  Briefcase,
  Bell,
  Settings as SettingsIcon,
  Award,
  AlertTriangle,
  Palette,
  Info,
} from "lucide-react";

import AssignmentRecommendationSettings from "./AssignmentRecommendationSettings";
import HealthScoreThresholdsOrgSettings from "./HealthScoreThresholdsOrgSettings";
import LowScoreCriteriaOrgSettings from "./LowScoreCriteriaOrgSettings";
import PortfolioNeedsAttentionOrgSettings from "./PortfolioNeedsAttentionOrgSettings";
import SponsorEngagementProgram from "./SponsorEngagementProgram";
import TeamAssessmentOrgSettings from "./TeamAssessmentOrgSettings";
import WeeklyDigestOrgSettings from "./WeeklyDigestOrgSettings";
import Button from "../../Components/Button";
import DropdownSelector from "../../Components/DropdownSelector";
import Icon from "../../Components/Icon";
import CommonInput from "../../Components/Input";
import PageHeader from "../../Components/PageHeader";
import ToggleSwitch from "../../Components/ToggleSwitch";
import EmailTemplatePreviewModal from "../../Container/EmailActivityContainer/EmailTemplatePreviewModal";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { fetchEmailActivityTemplates } from "../../Store/Reducers/EmailActivitySlice";
import {
  fetchNotificationSettings,
  updateNotificationSettings,
  sendAdminTestEmail,
  clearError as clearNotificationError,
} from "../../Store/Reducers/NotificationSlice";
import {
  fetchVendorAdminStatistics,
  fetchVendorAdminGrades,
  fetchVendorAdminAccountTiers,
  updateGrade,
  updateAccountTier,
  fetchColorCodingThresholds,
  updateColorCodingThresholds,
  clearColorCodingUpdateState,
  fetchAccountTeamRoleLimits,
  updateAccountTeamRoleLimits,
  fetchRlSettingConfig,
  fetchOrganizationSettings,
  fetchWeeklyDigestStatus,
  updateRlSettingConfig,
  type AccountTeamRoleLimits,
} from "../../Store/Reducers/VendorAdminSlice";
import { findEmailTemplateByType } from "../../Utils/Constants/EmailTypeLabels";
import {
  getAccountTeamRoleDisplayLabel,
  isRole,
  ROLES,
} from "../../Utils/Constants/RoleConstants";
import {
  STORAGE_KEYS,
  getFromLocalStorage,
} from "../../Utils/Helper/LocalStorage";
import {
  DEFAULT_FAN_IN_CAP,
  DEFAULT_FAN_OUT_CAPS,
  DEFAULT_OVERALL_FAN_OUT_CAP,
  VENDOR_GRADE_LEVELS,
  normalizeFanInCap,
  normalizeFanOutCaps,
  normalizeOverallFanOutCap,
} from "../../Utils/Helper/rlFanCaps";
import { toast } from "../../Utils/Helper/toast";

import "./style.scss";

const Settings: React.FC = () => {
  const dispatch = useAppDispatch();
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const userRole = loggedInUser?.role;
  const isExecutiveSponsorLead = isRole(userRole, ROLES.EXECUTIVE_SPONSOR_LEAD);
  const {
    statistics,
    grades,
    accountTiers,
    accountTiersLoading,
    loading,
    updateLoading,
    colorCodingThresholds: reduxColorCodingThresholds,
    colorCodingThresholdId,
    colorCodingLoading,
    colorCodingUpdateLoading,
    colorCodingUpdateError,
    colorCodingUpdateSuccess,
    accountTeamRoleLimits,
    accountTeamRoleLimitsLoading,
    accountTeamRoleLimitsUpdateLoading,
    accountTeamRoleLimitsConfigKey,
    rlSettingValue,
    rlSettingConfigKey,
    rlSettingLoading,
    rlSettingError,
    rlSettingUpdateLoading,
    organizationSettings,
    weeklyDigestStatus,
    weeklyDigestStatusLoading,
  } = useAppSelector((state: any) => state.vendorAdmin);
  const {
    settings: notificationSettings,
    loading: notificationLoading,
    sendEmailLoading,
    sendEmailError,
  } = useAppSelector((state: any) => state.notification);
  const { templates, templatesFetched } = useAppSelector(
    state => state.emailActivity
  );

  const [isEditingGrades, setIsEditingGrades] = useState(false);
  const [isEditingTiers, setIsEditingTiers] = useState(false);
  const [isEditingCapacity, setIsEditingCapacity] = useState(false);
  const [gradesData, setGradesData] = useState<any[]>([]);
  const [tiersData, setTiersData] = useState<any[]>([]);
  const [capacityData, setCapacityData] = useState<any[]>([]);
  const [accountTeamRoleLimitsData, setAccountTeamRoleLimitsData] =
    useState<AccountTeamRoleLimits>({});

  const cloneRows = (rows: any[]) => rows.map(item => ({ ...item }));
  const cloneRoleLimits = (
    limits: AccountTeamRoleLimits
  ): AccountTeamRoleLimits => ({ ...limits });

  const [tempGradesData, setTempGradesData] = useState(cloneRows(gradesData));
  const [tempTiersData, setTempTiersData] = useState(cloneRows(tiersData));
  const [tempCapacityData, setTempCapacityData] = useState(
    cloneRows(capacityData)
  );
  const [tempAccountTeamRoleLimitsData, setTempAccountTeamRoleLimitsData] =
    useState<AccountTeamRoleLimits>(cloneRoleLimits(accountTeamRoleLimitsData));
  const accountTeamRoleLimitEntries = useMemo(
    () =>
      Object.entries(accountTeamRoleLimitsData).sort(([a], [b]) =>
        a.localeCompare(b)
      ),
    [accountTeamRoleLimitsData]
  );

  const tempAccountTeamRoleLimitEntries = useMemo(
    () =>
      Object.entries(tempAccountTeamRoleLimitsData).sort(([a], [b]) =>
        a.localeCompare(b)
      ),
    [tempAccountTeamRoleLimitsData]
  );

  const emailTemplateOptions = [
    { label: "Welcome Email", value: "welcome_email" },
    { label: "Assignment Acknowledgement", value: "assignment_ack_email" },
    { label: "Reminder Activation Email", value: "prep_activation_email" },
    { label: "Reactivation Email", value: "reactivation_email" },
    { label: "User Invitation", value: "user_invitation_email" },
    { label: "Org Admin Invitation", value: "org_admin_invitation_email" },
    { label: "Easy Onboarding Invite", value: "easy_onboarding_invite_email" },
    { label: "Password Reset Link", value: "password_reset_link_email" },
    {
      label: "Password Reset Notification",
      value: "password_reset_notification",
    },
    {
      label: "Password Changed Confirmation",
      value: "password_changed_confirmation",
    },
    {
      label: "Sponsor Check-in Reminder (Poll 1)",
      value: "sponsor_engagement_poll1_reminder",
    },
    {
      label: "Team Feedback Reminder (Poll 2)",
      value: "sponsor_engagement_poll2_reminder",
    },
    {
      label: "Sponsor Check-in Requested (Poll 1)",
      value: "sponsor_engagement_poll1_launched",
    },
    {
      label: "Team Feedback Requested (Poll 2)",
      value: "sponsor_engagement_poll2_launched",
    },
    {
      label: "Leaderboard Published",
      value: "sponsor_engagement_leaderboard_published",
    },
    {
      label: "Batched Assessment Assigned",
      value: "batched_assessment_assigned_email",
    },
    {
      label: "Batched Assessment Extended",
      value: "batched_assessment_extended_email",
    },
    {
      label: "Batched Assessment Aborted",
      value: "batched_assessment_aborted_email",
    },
    { label: "Assessment Due Today", value: "assessment_due_today_email" },
    {
      label: "All Ongoing Assessment Due (Weekly)",
      value: "all_ongoing_assessment_due_email",
    },
    { label: "Assessment Published", value: "assessment_published_email" },
    { label: "Formal Due Reminder", value: "formal_due_reminder_email" },
    { label: "Team Assessment Due Reminder", value: "team_due_reminder_email" },
    {
      label: "Team Assessment Publish Cron Failed",
      value: "team_cron_publish_failed",
    },
    {
      label: "Team Assessment Auto Triggered",
      value: "team_assessment_auto_triggered_email",
    },
    { label: "Your Cadence Is Stale", value: "team_your_cadence_stale_email" },
    {
      label: "Monday Morning Briefing (Weekly Digest)",
      value: "weekly_digest_email",
    },
    {
      label: "Team Assessment Published (Participant)",
      value: "self_assessment_published_participant_email",
    },
    { label: "Disconnect Triggered", value: "disconnect_triggered_email" },
    {
      label: "Disconnect Due Reminder",
      value: "disconnect_due_reminder_email",
    },
    {
      label: "Disconnect Resolution Recorded",
      value: "disconnect_resolution_recorded_email",
    },
    { label: "Disconnect Published", value: "disconnect_published_email" },
    {
      label: "Account Plan Published",
      value: "account_plan_published_email",
    },
  ];
  const [recipientEmail, setRecipientEmail] = useState("");
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState(
    emailTemplateOptions[0].value
  );
  const [recipientError, setRecipientError] = useState("");
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);

  const selectedPreviewTemplate = useMemo(
    () => findEmailTemplateByType(templates, selectedEmailTemplate),
    [templates, selectedEmailTemplate]
  );

  // Color Coding Settings state
  const [colorCodingThresholds, setColorCodingThresholds] = useState({
    utilization_percentage: {
      greenThreshold: 100.0,
      yellowThreshold: 120.0,
    },
  });
  const [originalColorCodingThresholds, setOriginalColorCodingThresholds] =
    useState<any>({});
  const [isEditingColorCoding, setIsEditingColorCoding] = useState(false);

  // Notification settings state
  const [notificationTimeRange, setNotificationTimeRange] =
    useState<number>(168);
  const [considerMeddpiccRoleEnabled, setConsiderMeddpiccRoleEnabled] =
    useState(false);
  const [fanInCap, setFanInCap] = useState(DEFAULT_FAN_IN_CAP);
  const [fanOutCaps, setFanOutCaps] = useState<Record<string, number>>({
    ...DEFAULT_FAN_OUT_CAPS,
  });
  const [overallFanOutCap, setOverallFanOutCap] = useState(
    DEFAULT_OVERALL_FAN_OUT_CAP
  );
  const [isEditingFanCaps, setIsEditingFanCaps] = useState(false);
  const [fanInCapDraft, setFanInCapDraft] = useState(
    String(DEFAULT_FAN_IN_CAP)
  );
  const [fanOutCapDrafts, setFanOutCapDrafts] = useState<
    Record<string, string>
  >(
    Object.fromEntries(
      VENDOR_GRADE_LEVELS.map(grade => [
        String(grade),
        String(DEFAULT_FAN_OUT_CAPS[String(grade)]),
      ])
    )
  );
  const [overallFanOutCapDraft, setOverallFanOutCapDraft] = useState(
    String(DEFAULT_OVERALL_FAN_OUT_CAP)
  );

  // Helper function to format time range description
  const getTimeRangeDescription = (hours: number): string => {
    if (!hours || isNaN(hours)) return "1 week";
    if (hours === 12) return "12 hours";
    if (hours === 24) return "1 day";
    if (hours === 48) return "2 days";
    if (hours === 72) return "3 days";
    if (hours === 168) return "1 week";
    const days = Math.floor(hours / 24);
    return days === 1 ? "1 day" : `${days} days`;
  };

  const notificationTimeOptions = [
    { label: "12 Hours", value: 12 },
    { label: "24 Hours", value: 24 },
    { label: "48 Hours", value: 48 },
    { label: "72 Hours", value: 72 },
    { label: "168 Hours (1 week)", value: 168 },
  ];

  useEffect(() => {
    dispatch(fetchVendorAdminStatistics());
    dispatch(fetchVendorAdminGrades());
    dispatch(fetchVendorAdminAccountTiers());
    dispatch(fetchNotificationSettings());
    dispatch(fetchColorCodingThresholds());
    dispatch(fetchAccountTeamRoleLimits());
    dispatch(fetchRlSettingConfig());
    dispatch(fetchOrganizationSettings());
    dispatch(fetchWeeklyDigestStatus());
  }, [dispatch]);

  useEffect(() => {
    if (!isExecutiveSponsorLead && !templatesFetched) {
      dispatch(fetchEmailActivityTemplates() as any);
    }
  }, [dispatch, isExecutiveSponsorLead, templatesFetched]);

  // Update local state when Redux state changes
  useEffect(() => {
    if (reduxColorCodingThresholds) {
      setColorCodingThresholds(reduxColorCodingThresholds);
      setOriginalColorCodingThresholds(
        JSON.parse(JSON.stringify(reduxColorCodingThresholds))
      );
    }
  }, [reduxColorCodingThresholds]);

  // Memoize derived values to prevent unnecessary re-renders
  const hasColorCodingChanges = useMemo(() => {
    return (
      JSON.stringify(colorCodingThresholds) !==
      JSON.stringify(originalColorCodingThresholds)
    );
  }, [colorCodingThresholds, originalColorCodingThresholds]);

  // Handle color coding update success
  useEffect(() => {
    if (colorCodingUpdateSuccess) {
      toast.success("Color coding thresholds have been updated successfully.");
      setOriginalColorCodingThresholds(
        JSON.parse(JSON.stringify(colorCodingThresholds))
      );
      setIsEditingColorCoding(false);
      dispatch(clearColorCodingUpdateState());
    }
  }, [colorCodingUpdateSuccess, colorCodingThresholds, dispatch]);

  // Handle color coding update error
  useEffect(() => {
    if (colorCodingUpdateError) {
      toast.error(
        colorCodingUpdateError ||
          "Failed to save color coding thresholds. Please try again."
      );
      dispatch(clearColorCodingUpdateState());
    }
  }, [colorCodingUpdateError, dispatch]);

  useEffect(() => {
    if (notificationSettings) {
      const timeRange = notificationSettings.notificationTimeRangeHours || 168;
      setNotificationTimeRange(timeRange);
    }
  }, [notificationSettings]);

  useEffect(() => {
    if (!rlSettingValue) return;
    setConsiderMeddpiccRoleEnabled(
      Boolean(rlSettingValue.consider_meddpicc_role)
    );
    if (!isEditingFanCaps) {
      setFanInCap(normalizeFanInCap(rlSettingValue.fan_in_cap));
      setFanOutCaps(normalizeFanOutCaps(rlSettingValue.fan_out_caps));
      setOverallFanOutCap(
        normalizeOverallFanOutCap(rlSettingValue.overall_fan_out_cap)
      );
    }
  }, [rlSettingValue, isEditingFanCaps]);

  useEffect(() => {
    if (rlSettingError) {
      toast.error(rlSettingError);
    }
  }, [rlSettingError]);

  useEffect(() => {
    if (grades?.items) {
      const mappedGrades = grades.items.map((item: any) => ({
        grade: `GRADE ${item.grade}`,
        label: item.grade_name,
        id: item.grade_id,
      }));
      const mappedCapacity = grades.items.map((item: any) => ({
        grade: item.grade_name,
        maxAccounts: item.capacity_limits,
        id: item.grade_id,
      }));
      setGradesData(mappedGrades);
      setCapacityData(mappedCapacity);
      setTempGradesData(cloneRows(mappedGrades));
      setTempCapacityData(cloneRows(mappedCapacity));
    }
  }, [grades]);

  useEffect(() => {
    if (accountTiers?.items) {
      const mappedTiers = accountTiers.items.map((item: any) => ({
        tier: item.tier_code,
        label: item.tier_name,
        id: item.tier_id,
      }));
      setTiersData(mappedTiers);
      setTempTiersData(cloneRows(mappedTiers));
    }
  }, [accountTiers]);

  useEffect(() => {
    setAccountTeamRoleLimitsData(accountTeamRoleLimits);
    setTempAccountTeamRoleLimitsData(cloneRoleLimits(accountTeamRoleLimits));
  }, [accountTeamRoleLimits]);

  const handleNotificationTimeRangeChange = (raw: string) => {
    const value = parseInt(raw, 10);
    setNotificationTimeRange(value);
    handleNotificationSettingsUpdate(value);
  };

  const handleNotificationSettingsUpdate = async (timeRange: number) => {
    await dispatch(
      updateNotificationSettings({
        notification_time_range_hours: timeRange,
      })
    );
  };

  const handleRlSettingsUpdate = async (nextConsiderMeddpiccRole: boolean) => {
    try {
      await dispatch(
        updateRlSettingConfig({
          key: rlSettingConfigKey || "rl_setting_config",
          value: {
            relationship_assessments: Boolean(
              organizationSettings?.rl_setting_config
                ?.relationship_assessments ??
                rlSettingValue?.relationship_assessments
            ),
            consider_meddpicc_role: nextConsiderMeddpiccRole,
          },
        })
      ).unwrap();
    } catch (error) {
      toast.error(
        typeof error === "string"
          ? error
          : "Failed to save RL assessment settings."
      );
      setConsiderMeddpiccRoleEnabled(
        Boolean(rlSettingValue?.consider_meddpicc_role)
      );
    }
  };

  const handleConsiderMeddpiccRoleToggle = (checked: boolean) => {
    setConsiderMeddpiccRoleEnabled(checked);
    void handleRlSettingsUpdate(checked);
  };

  const gradeFanOutRows = useMemo(() => {
    const byNumber = new Map(
      (grades?.items || []).map((item: any) => [Number(item.grade), item])
    );
    return VENDOR_GRADE_LEVELS.map(grade => {
      const item = byNumber.get(grade);
      return {
        grade,
        label: (item as any)?.grade_name || `Grade ${grade}`,
      };
    });
  }, [grades]);

  const handleFanOutCapDraftChange = (grade: number, raw: string) => {
    setFanOutCapDrafts(current => ({
      ...current,
      [String(grade)]: raw,
    }));
  };

  const handleEditFanCaps = () => {
    const nextFanIn = normalizeFanInCap(rlSettingValue?.fan_in_cap ?? fanInCap);
    const nextFanOut = normalizeFanOutCaps(
      rlSettingValue?.fan_out_caps ?? fanOutCaps
    );
    const nextOverallFanOut = normalizeOverallFanOutCap(
      rlSettingValue?.overall_fan_out_cap ?? overallFanOutCap
    );
    setFanInCap(nextFanIn);
    setFanOutCaps(nextFanOut);
    setOverallFanOutCap(nextOverallFanOut);
    setFanInCapDraft(String(nextFanIn));
    setFanOutCapDrafts(
      Object.fromEntries(
        VENDOR_GRADE_LEVELS.map(grade => [
          String(grade),
          String(
            nextFanOut[String(grade)] ?? DEFAULT_FAN_OUT_CAPS[String(grade)]
          ),
        ])
      )
    );
    setOverallFanOutCapDraft(String(nextOverallFanOut));
    setIsEditingFanCaps(true);
  };

  const handleCancelFanCaps = () => {
    setFanInCap(normalizeFanInCap(rlSettingValue?.fan_in_cap));
    setFanOutCaps(normalizeFanOutCaps(rlSettingValue?.fan_out_caps));
    setOverallFanOutCap(
      normalizeOverallFanOutCap(rlSettingValue?.overall_fan_out_cap)
    );
    setIsEditingFanCaps(false);
  };

  const handleRlCoverageCapsSave = async () => {
    try {
      await dispatch(
        updateRlSettingConfig({
          key: rlSettingConfigKey || "rl_setting_config",
          value: {
            fan_in_cap: normalizeFanInCap(fanInCapDraft),
            fan_out_caps: normalizeFanOutCaps(fanOutCapDrafts),
            overall_fan_out_cap: normalizeOverallFanOutCap(
              overallFanOutCapDraft
            ),
          },
        })
      ).unwrap();
      setIsEditingFanCaps(false);
      toast.success("RL coverage caps saved");
    } catch (error) {
      toast.error(
        typeof error === "string" ? error : "Failed to save RL coverage caps."
      );
    }
  };

  const getTemplateLabel = (value: string) => {
    const option = emailTemplateOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  const handleRecipientChange = (value: string) => {
    setRecipientEmail(value);
    if (recipientError) setRecipientError("");
    if (sendEmailError) dispatch(clearNotificationError());
  };

  const handleSendTestEmail = async () => {
    const trimmedEmail = recipientEmail.trim();

    if (!trimmedEmail) {
      setRecipientError("Recipient email required");
      return;
    }

    try {
      await dispatch(
        sendAdminTestEmail({
          email_type: selectedEmailTemplate,
          recipient_email: trimmedEmail,
        })
      ).unwrap();
      toast.success(
        `A ${getTemplateLabel(selectedEmailTemplate)} was sent to ${trimmedEmail}.`
      );
      setRecipientEmail("");
      setRecipientError("");
    } catch (error: any) {
      let details =
        "Failed to send test email. Please verify the address and try again.";
      try {
        const parsedError = JSON.parse(error?.message);
        if (parsedError?.message) {
          details = parsedError.message;
        }
      } catch (parseError) {
        // ignore parse error, fallback message is already set
      }
      setRecipientError(details);
      toast.error(details);
    }
  };

  const handlePreviewTemplate = () => {
    const trimmedEmail = recipientEmail.trim();

    if (!trimmedEmail) {
      setRecipientError("Recipient email required");
      return;
    }

    setRecipientError("");
    setShowTemplatePreview(true);
  };

  const metricData = [
    {
      title: "Total Accounts",
      value: statistics?.total_activated_accounts || 0,
      icon: Building2,
      colorClass: "text-purple-600",
      bgColorClass: "bg-purple-50",
    },
    {
      title: "Total Executives",
      value: statistics?.total_activated_executives || 0,
      icon: Users,
      colorClass: "text-green-600",
      bgColorClass: "bg-green-50",
    },
    {
      title: "Total Assignments",
      value: statistics?.total_active_assignments || 0,
      icon: Briefcase,
      colorClass: "text-blue-600",
      bgColorClass: "bg-blue-50",
    },
  ];

  const handleEditGrades = () => {
    setTempGradesData(cloneRows(gradesData));
    setIsEditingGrades(true);
  };

  const handleCancelGrades = () => {
    setTempGradesData(cloneRows(gradesData));
    setIsEditingGrades(false);
  };

  const handleSaveGrades = async () => {
    const originalGradesById = new Map(
      gradesData.map(item => [String(item.id), item.label])
    );
    const changedGrades = tempGradesData.filter(item => {
      const originalLabel = originalGradesById.get(String(item.id));
      return originalLabel !== undefined && item.label !== originalLabel;
    });

    if (changedGrades.length === 0) {
      setIsEditingGrades(false);
      return;
    }

    try {
      for (const item of changedGrades) {
        if (!item.id) continue;
        const matchingCapacity = tempCapacityData.find(
          c => String(c.id) === String(item.id)
        );
        await dispatch(
          updateGrade({
            grade_id: item.id,
            grade_name: item.label,
            capacity_limits: matchingCapacity?.maxAccounts || 0,
          })
        ).unwrap();
      }

      await dispatch(fetchVendorAdminGrades());
      setGradesData([...tempGradesData]);
      setIsEditingGrades(false);
      toast.success("The settings have been updated successfully.");
    } catch (error) {
      toast.error(
        typeof error === "string"
          ? error
          : "An error occurred while updating. Please try again."
      );
    }
  };

  const handleGradeChange = (index: number, value: string) => {
    const updated = [...tempGradesData];
    updated[index].label = value;
    setTempGradesData(updated);
  };

  const handleEditTiers = () => {
    setTempTiersData(cloneRows(tiersData));
    setIsEditingTiers(true);
  };

  const handleCancelTiers = () => {
    setTempTiersData(cloneRows(tiersData));
    setIsEditingTiers(false);
  };

  const handleSaveTiers = async () => {
    const originalTiersById = new Map(
      tiersData.map(item => [String(item.id), item.label])
    );
    const changedTiers = tempTiersData.filter(item => {
      const originalLabel = originalTiersById.get(String(item.id));
      return originalLabel !== undefined && item.label !== originalLabel;
    });

    if (changedTiers.length === 0) {
      setIsEditingTiers(false);
      return;
    }

    try {
      for (const item of changedTiers) {
        if (!item.id) continue;
        await dispatch(
          updateAccountTier({
            tier_id: item.id,
            tier_name: item.label,
          })
        ).unwrap();
      }

      await dispatch(fetchVendorAdminAccountTiers());
      setTiersData([...tempTiersData]);
      setIsEditingTiers(false);
      toast.success("The settings have been updated successfully.");
    } catch (error) {
      toast.error(
        typeof error === "string"
          ? error
          : "An error occurred while updating. Please try again."
      );
    }
  };

  const handleTierChange = (index: number, value: string) => {
    const updated = [...tempTiersData];
    updated[index].label = value;
    setTempTiersData(updated);
  };

  const handleEditCapacity = () => {
    setTempCapacityData(cloneRows(capacityData));
    setTempAccountTeamRoleLimitsData(
      cloneRoleLimits(accountTeamRoleLimitsData)
    );
    setIsEditingCapacity(true);
  };

  const handleCancelCapacity = () => {
    setTempCapacityData(cloneRows(capacityData));
    setTempAccountTeamRoleLimitsData(
      cloneRoleLimits(accountTeamRoleLimitsData)
    );
    setIsEditingCapacity(false);
  };

  const handleSaveCapacity = async () => {
    const originalCapacityById = new Map(
      capacityData.map(item => [String(item.id), Number(item.maxAccounts)])
    );
    const changedCapacities = tempCapacityData.filter(item => {
      const originalCapacity = originalCapacityById.get(String(item.id));
      return (
        originalCapacity !== undefined &&
        Number(item.maxAccounts) !== originalCapacity
      );
    });

    const roleLimitsChanged =
      JSON.stringify(tempAccountTeamRoleLimitsData) !==
      JSON.stringify(accountTeamRoleLimitsData);

    try {
      for (const item of changedCapacities) {
        if (!item.id) continue;
        const matchingGrade = tempGradesData.find(
          g => String(g.id) === String(item.id)
        );
        await dispatch(
          updateGrade({
            grade_id: item.id,
            grade_name: matchingGrade?.label || item.grade,
            capacity_limits: item.maxAccounts,
          })
        ).unwrap();
      }

      if (changedCapacities.length > 0) {
        await dispatch(fetchVendorAdminGrades());
      }

      if (roleLimitsChanged) {
        if (!accountTeamRoleLimitsConfigKey) {
          toast.error("Account team role limits config is not loaded yet.");
          return;
        }
        await dispatch(
          updateAccountTeamRoleLimits({
            key: accountTeamRoleLimitsConfigKey,
            value: tempAccountTeamRoleLimitsData,
          })
        ).unwrap();
      }

      if (changedCapacities.length > 0 || roleLimitsChanged) {
        toast.success("Capacity limits updated successfully.");
      }

      setCapacityData([...tempCapacityData]);
      setAccountTeamRoleLimitsData(
        cloneRoleLimits(tempAccountTeamRoleLimitsData)
      );
      setIsEditingCapacity(false);
    } catch (error) {
      toast.error(
        typeof error === "string"
          ? error
          : "Failed to save capacity limits. Please try again."
      );
    }
  };

  const handleCapacityChange = (index: number, value: string) => {
    const updated = [...tempCapacityData];
    const numValue = parseInt(value) || 0;
    updated[index].maxAccounts = numValue;
    setTempCapacityData(updated);
  };

  const handleAccountTeamRoleLimitChange = (role: string, value: string) => {
    const numValue = Math.max(0, parseInt(value, 10) || 0);
    setTempAccountTeamRoleLimitsData(prev => ({
      ...prev,
      [role]: numValue,
    }));
  };

  // Color Coding Settings functions

  const handleColorCodingThresholdChange = (
    metricType: string,
    field: string,
    value: string
  ) => {
    const numValue = parseFloat(value) || 0;
    setColorCodingThresholds((prev: any) => ({
      ...prev,
      [metricType]: {
        ...prev[metricType],
        [field]: numValue,
      },
    }));
  };

  const validateColorCodingThresholds = () => {
    for (const [metricType, config] of Object.entries(colorCodingThresholds)) {
      const typedConfig = config as any;
      if (typedConfig.yellowThreshold <= typedConfig.greenThreshold) {
        toast.error(
          `${metricType}: Yellow threshold must be greater than green threshold`
        );
        return false;
      }
    }
    return true;
  };

  const handleEditColorCoding = () => {
    setOriginalColorCodingThresholds(
      JSON.parse(JSON.stringify(colorCodingThresholds))
    );
    setIsEditingColorCoding(true);
  };

  const handleCancelColorCoding = () => {
    setColorCodingThresholds(
      JSON.parse(JSON.stringify(originalColorCodingThresholds))
    );
    setIsEditingColorCoding(false);
  };

  const handleSaveColorCoding = async () => {
    if (!validateColorCodingThresholds()) return;

    // Get the utilization_percentage config
    const utilizationConfig = colorCodingThresholds.utilization_percentage;

    // Get threshold ID from Redux state or from the thresholds object
    const thresholdId =
      colorCodingThresholdId || (colorCodingThresholds as any)?.id;

    if (!utilizationConfig || !thresholdId) {
      toast.error(
        "Threshold ID is missing. Please refresh the page and try again."
      );
      return;
    }

    // Update with the correct payload format
    await dispatch(
      updateColorCodingThresholds({
        thresholdId: thresholdId,
        data: {
          green_zone_upper_limit: utilizationConfig.greenThreshold,
          yellow_zone_upper_limit: utilizationConfig.yellowThreshold,
        },
      })
    );

    // Trigger a refresh of any components using these thresholds
    window.dispatchEvent(new CustomEvent("thresholdsUpdated"));
  };

  return (
    <div className="settings-page bg-white overflow-hidden mt-3">
      <PageHeader title="Settings" icon={SettingsIcon} />
      <div className="w-full bg-white p-3 md:p-4">
        <div className="settings-container rounded-md bg-gray-100 p-4 md:p-4">
          {/* System Overview Section */}
          {!isExecutiveSponsorLead && (
            <div className="settings-section bg-white p-6 rounded-lg shadow">
              <div className="settings-section__header">
                <div className="flex items-center gap-2">
                  <Icon
                    className="primary-color"
                    name="database"
                    style={{ height: 20, width: 20 }}
                  />
                  <h2 className="fs-18 fw-600 mb-0">System Overview</h2>
                </div>
              </div>
              <div className="settings-section__content">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {metricData.map((metric, index) => {
                    const IconComponent = metric.icon;
                    return (
                      <div
                        key={index}
                        className={`${metric.bgColorClass} rounded-lg p-4 border border-gray-200`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <IconComponent
                            className={`${metric.colorClass} w-5 h-5`}
                            size={20}
                          />
                          <div
                            className={`text-base font-medium ${metric.colorClass}`}
                          >
                            {metric.title}
                          </div>
                        </div>
                        <div
                          className={`text-3xl font-semibold ${metric.colorClass}`}
                        >
                          {metric.value}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Tier Settings Section */}
          <div className="settings-section bg-white p-6 rounded-lg shadow">
            <div className="settings-section__header">
              <div className="flex items-center justify-between w-full">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <Icon
                      className="primary-color"
                      name="settings"
                      style={{ height: 20, width: 20 }}
                    />
                    <h2 className="fs-18 fw-600 mb-0">Tier Settings</h2>
                  </div>
                  <p className="text-sm text-gray-600">
                    Configure how account tiers are displayed in your
                    organization. Each tier can have a custom label that
                    reflects your organization&apos;s terminology.
                  </p>
                </div>
                {!isEditingTiers ? (
                  <Button
                    variant="outline-primary"
                    onClick={handleEditTiers}
                    className="settings-edit-btn"
                  >
                    <Icon
                      name="pencilPrimary"
                      style={{ width: "0.875rem", height: "0.875rem" }}
                    />
                    Edit
                  </Button>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="primary"
                      onClick={handleSaveTiers}
                      className="settings-save-btn"
                      loading={updateLoading}
                    >
                      Save
                    </Button>
                    <Button
                      variant="gray"
                      onClick={handleCancelTiers}
                      className="settings-cancel-btn"
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div className="settings-section__content">
              {loading || accountTiersLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : !isEditingTiers ? (
                <div className="grades-list">
                  {tiersData.map((item, index) => (
                    <div key={index} className="grade-item">
                      <span className="grade-item__grade">{item.tier}:</span>
                      <span className="grade-item__label">{item.label}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grades-edit-list">
                  {tempTiersData.map((item, index) => (
                    <div key={index} className="grade-edit-item">
                      <span className="grade-edit-item__grade">
                        {item.tier}:
                      </span>
                      <CommonInput
                        variant="field"
                        name={`tier-${index}`}
                        value={item.label}
                        onChange={e => handleTierChange(index, e.target.value)}
                        className="grade-edit-item__input"
                      />
                    </div>
                  ))}
                  <div className="grade-info-banner">
                    <Icon
                      name="completeSuccess"
                      className="grade-info-banner__icon"
                    />
                    <div className="grade-info-banner__content">
                      <div className="grade-info-banner__title">
                        Tier Configuration
                      </div>
                      <div className="grade-info-banner__description">
                        These labels will be used throughout the system to
                        display account tiers. Changes will be reflected
                        immediately in account listings, filters, and forms.
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Grade Settings Section */}
          <div className="settings-section bg-white p-6 rounded-lg shadow">
            <div className="settings-section__header">
              <div className="flex items-center justify-between w-full">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <Icon
                      className="primary-color"
                      name="settings"
                      style={{ height: 20, width: 20 }}
                    />
                    <h2 className="fs-18 fw-600 mb-0">Grade Settings</h2>
                  </div>
                  <p className="text-sm text-gray-600">
                    Configure how grades are displayed in your organization.
                    Each grade can have a custom label that reflects your
                    organization&apos;s terminology.
                  </p>
                </div>
                {!isEditingGrades ? (
                  <Button
                    variant="outline-primary"
                    onClick={handleEditGrades}
                    className="settings-edit-btn"
                  >
                    <Icon
                      name="pencilPrimary"
                      style={{ width: "0.875rem", height: "0.875rem" }}
                    />
                    Edit
                  </Button>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="primary"
                      onClick={handleSaveGrades}
                      className="settings-save-btn"
                      loading={updateLoading}
                    >
                      Save
                    </Button>
                    <Button
                      variant="gray"
                      onClick={handleCancelGrades}
                      className="settings-cancel-btn"
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div className="settings-section__content">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : !isEditingGrades ? (
                <div className="grades-list">
                  {gradesData.map((item, index) => (
                    <div key={index} className="grade-item">
                      <span className="grade-item__grade">{item.grade}:</span>
                      <span className="grade-item__label">{item.label}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grades-edit-list">
                  {tempGradesData.map((item, index) => (
                    <div key={index} className="grade-edit-item">
                      <span className="grade-edit-item__grade">
                        {item.grade}:
                      </span>
                      <CommonInput
                        variant="field"
                        name={`grade-${index}`}
                        value={item.label}
                        onChange={e => handleGradeChange(index, e.target.value)}
                        className="grade-edit-item__input"
                      />
                    </div>
                  ))}
                  <div className="grade-info-banner">
                    <Icon
                      name="completeSuccess"
                      className="grade-info-banner__icon"
                    />
                    <div className="grade-info-banner__content">
                      <div className="grade-info-banner__title">
                        Grade Configuration
                      </div>
                      <div className="grade-info-banner__description">
                        These labels will be used throughout the system to
                        display grades for executives. Changes will be reflected
                        immediately in all executive listings, reports, and
                        forms.
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Capacity Limits Section */}
          <div className="settings-section bg-white p-6 rounded-lg shadow">
            <div className="settings-section__header">
              <div className="flex items-center justify-between w-full">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <Icon
                      className="primary-color"
                      name="users"
                      style={{ height: 20, width: 20 }}
                    />
                    <h2 className="fs-18 fw-600 mb-0">Capacity Limits</h2>
                  </div>
                  <p className="text-sm text-gray-600">
                    Set maximum thresholds per grade and per account team role.
                    These limits support capacity planning and assignment
                    recommendations.
                  </p>
                </div>
                {!isEditingCapacity ? (
                  <Button
                    variant="outline-primary"
                    onClick={handleEditCapacity}
                    className="settings-edit-btn"
                  >
                    <Icon
                      name="pencilPrimary"
                      style={{ width: "0.875rem", height: "0.875rem" }}
                    />
                    Edit
                  </Button>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="primary"
                      onClick={handleSaveCapacity}
                      className="settings-save-btn"
                      loading={
                        updateLoading || accountTeamRoleLimitsUpdateLoading
                      }
                    >
                      Save
                    </Button>
                    <Button
                      variant="gray"
                      onClick={handleCancelCapacity}
                      className="settings-cancel-btn"
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div className="settings-section__content">
              {loading || accountTeamRoleLimitsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : !isEditingCapacity ? (
                <div className="grid grid-cols-1 gap-8 xl:grid-cols-2">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">
                      Account Team Role Limits
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      Maximum team members per role on each account.
                    </p>
                    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {accountTeamRoleLimitEntries.length === 0 ? (
                        <p className="text-sm text-gray-500 sm:col-span-2">
                          No account team role limits configured.
                        </p>
                      ) : (
                        accountTeamRoleLimitEntries.map(([role, limit]) => (
                          <div key={role} className="bg-gray-50 rounded-lg p-4">
                            <div className="text-sm font-medium text-gray-900">
                              {role}{" "}
                              <span className="font-normal text-gray-500">
                                ({getAccountTeamRoleDisplayLabel(role)})
                              </span>
                            </div>
                            <div className="mt-2 text-2xl font-semibold text-gray-900">
                              {limit}
                            </div>
                            <div className="text-sm text-gray-500">
                              max per account
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-base font-semibold text-gray-900">
                      Grade Limits
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      Maximum accounts each executive grade can handle.
                    </p>
                    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {capacityData.map((item, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Award className="h-4 w-4 text-purple-600" />
                            <span className="text-sm font-medium text-gray-900">
                              {item.grade}
                            </span>
                          </div>
                          <div className="text-2xl font-semibold text-gray-900">
                            {item.maxAccounts}
                          </div>
                          <div className="text-sm text-gray-500">
                            accounts max
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-8 xl:grid-cols-2">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">
                        Account Team Role Limits
                      </h3>
                      <p className="mt-1 text-sm text-gray-600">
                        Maximum team members per role on each account.
                      </p>
                      <div className="capacity-limits-edit mt-4">
                        {tempAccountTeamRoleLimitEntries.length === 0 ? (
                          <p className="text-sm text-gray-500">
                            No account team role limits configured.
                          </p>
                        ) : (
                          tempAccountTeamRoleLimitEntries.map(
                            ([role, limit]) => (
                              <div
                                key={role}
                                className="capacity-limit-edit-item"
                              >
                                <div className="capacity-limit-edit-item__header">
                                  <Briefcase className="h-4 w-4 text-purple-600" />
                                  <span className="capacity-limit-edit-item__label">
                                    {role} (
                                    {getAccountTeamRoleDisplayLabel(role)})
                                  </span>
                                </div>
                                <CommonInput
                                  variant="field"
                                  name={`account-team-role-${role}`}
                                  type="number"
                                  min="0"
                                  value={limit.toString()}
                                  onChange={e =>
                                    handleAccountTeamRoleLimitChange(
                                      role,
                                      e.target.value
                                    )
                                  }
                                  className="capacity-limit-edit-item__input"
                                />
                                <span className="capacity-limit-edit-item__suffix">
                                  max per account
                                </span>
                              </div>
                            )
                          )
                        )}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-base font-semibold text-gray-900">
                        Grade Limits
                      </h3>
                      <p className="mt-1 text-sm text-gray-600">
                        Maximum accounts each executive grade can handle.
                      </p>
                      <div className="capacity-limits-edit mt-4">
                        {tempCapacityData.map((item, index) => (
                          <div key={index} className="capacity-limit-edit-item">
                            <div className="capacity-limit-edit-item__header">
                              <Award className="h-4 w-4 text-purple-600" />
                              <span className="capacity-limit-edit-item__label">
                                {item.grade}
                              </span>
                            </div>
                            <CommonInput
                              variant="field"
                              name={`capacity-${index}`}
                              type="number"
                              min="0"
                              value={item.maxAccounts.toString()}
                              onChange={e =>
                                handleCapacityChange(index, e.target.value)
                              }
                              className="capacity-limit-edit-item__input"
                            />
                            <span className="capacity-limit-edit-item__suffix">
                              accounts max
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="capacity-info-banner">
                    <Icon
                      name="completeSuccess"
                      className="capacity-info-banner__icon"
                    />
                    <div className="capacity-info-banner__content">
                      <div className="capacity-info-banner__title">
                        Capacity Configuration
                      </div>
                      <div className="capacity-info-banner__description">
                        <p className="mb-2">
                          <strong>Grade Limits:</strong> These limits determine
                          the maximum number of accounts each grade of executive
                          can handle. Changes will affect new executive
                          assignments and capacity calculations.
                        </p>
                        <p className="mb-0">
                          <strong>Account Team Role Limits:</strong> These
                          limits set the maximum number of team members allowed
                          per role on each account. They support capacity
                          planning and balanced resource allocation across
                          account teams.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Sponsor Engagement Program */}
          <SponsorEngagementProgram />

          {/* Assignment Recommendation Settings Section */}
          {!isExecutiveSponsorLead && <AssignmentRecommendationSettings />}

          {/* Team assessment settings (currency + disconnect workflow) */}
          <TeamAssessmentOrgSettings />

          {/* Portfolio Needs Attention (My Accounts) */}
          <PortfolioNeedsAttentionOrgSettings />

          {/* Business Health / Account Health score bands */}
          {!isExecutiveSponsorLead && <HealthScoreThresholdsOrgSettings />}

          {/* Relationship Priorities — Low Score criteria */}
          <LowScoreCriteriaOrgSettings />

          {/* Weekly digest (Monday Morning Briefing) */}
          {!isExecutiveSponsorLead &&
            !weeklyDigestStatusLoading &&
            weeklyDigestStatus != null && <WeeklyDigestOrgSettings />}

          {/* Notification Settings Section */}
          {!isExecutiveSponsorLead && (
            <div className="settings-section bg-white p-6 rounded-lg shadow">
              <div className="settings-section__header">
                <div className="flex items-center gap-2">
                  <SettingsIcon className="text-purple-600" size={20} />
                  <h2 className="fs-18 fw-600 mb-0">Notification Settings</h2>
                </div>
              </div>
              <div className="settings-section__content">
                {notificationLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  </div>
                ) : (
                  <div className="notification-settings">
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-base font-semibold text-gray-900">
                          Notification Time Range
                        </h3>
                        <div className="flex items-center gap-2">
                          <Bell className="text-gray-600" size={20} />
                          <div className="w-48">
                            <DropdownSelector
                              label="Notification Time Range"
                              selected={String(notificationTimeRange)}
                              options={notificationTimeOptions.map(opt => ({
                                label: opt.label,
                                value: String(opt.value),
                              }))}
                              onSelect={handleNotificationTimeRangeChange}
                              triggerStyle="field"
                              enableScroll
                              fullWidth
                              hideLabel
                            />
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600">
                        Show notifications from the last{" "}
                        {notificationTimeRange || 168} hours (
                        {getTimeRangeDescription(notificationTimeRange || 168)})
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* RL Assessment Settings Section */}
          <div className="settings-section bg-white p-6 rounded-lg shadow">
            <div className="settings-section__header">
              <div className="flex items-center gap-2">
                <SettingsIcon className="text-purple-600" size={20} />
                <h2 className="fs-18 fw-600 mb-0">RL Assessment Settings</h2>
              </div>
            </div>
            <div className="settings-section__content">
              {rlSettingLoading && !rlSettingValue ? (
                <div className="flex items-center justify-center py-6">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : (
                <div className="pb-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900 mb-1">
                        Consider MEDDPICC role?
                      </h3>
                      <p className="text-sm text-gray-600">
                        If disabled, MEDDPICC groups and tags are hidden and
                        aggregate RL uses a MEDDPICC-unknown baseline.
                      </p>
                    </div>
                    <ToggleSwitch
                      checked={considerMeddpiccRoleEnabled}
                      onChange={handleConsiderMeddpiccRoleToggle}
                      id="consider-meddpicc-role-toggle"
                      disabled={rlSettingLoading || rlSettingUpdateLoading}
                    />
                  </div>

                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div>
                        <h3 className="text-base font-semibold text-gray-900 mb-1">
                          FAN capacity configurations
                        </h3>
                        <p className="text-sm text-gray-600">
                          Warn-only caps for contact mapping. Fan-in and
                          (over-all) are org-wide. Each grade&apos;s Fan-Out is
                          the per-account limit for that grade.
                        </p>
                      </div>
                      {!isEditingFanCaps ? (
                        <Button
                          variant="outline-primary"
                          onClick={handleEditFanCaps}
                          className="settings-edit-btn shrink-0"
                          disabled={rlSettingLoading}
                        >
                          <Icon
                            name="pencilPrimary"
                            style={{ width: "0.875rem", height: "0.875rem" }}
                          />
                          Edit
                        </Button>
                      ) : (
                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            variant="primary"
                            onClick={handleRlCoverageCapsSave}
                            className="settings-save-btn"
                            loading={rlSettingUpdateLoading}
                            disabled={rlSettingUpdateLoading}
                          >
                            Save
                          </Button>
                          <Button
                            variant="gray"
                            onClick={handleCancelFanCaps}
                            className="settings-cancel-btn"
                            disabled={rlSettingUpdateLoading}
                          >
                            Cancel
                          </Button>
                        </div>
                      )}
                    </div>
                    {!isEditingFanCaps ? (
                      <div className="grades-list fan-cap-list">
                        <div className="grade-item">
                          <span className="grade-item__grade">Fan-in:</span>
                          <span className="grade-item__label">{fanInCap}</span>
                        </div>
                        {gradeFanOutRows.map(row => (
                          <div key={row.grade} className="grade-item">
                            <span className="grade-item__grade">
                              Fan-Out ({row.label}):
                            </span>
                            <span className="grade-item__label">
                              {fanOutCaps[String(row.grade)] ??
                                DEFAULT_FAN_OUT_CAPS[String(row.grade)]}
                            </span>
                          </div>
                        ))}
                        <div className="grade-item">
                          <span className="grade-item__grade">
                            Fan-out (over-all):
                          </span>
                          <span className="grade-item__label">
                            {overallFanOutCap}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="grades-edit-list fan-cap-list">
                        <div className="grade-edit-item">
                          <span className="grade-edit-item__grade">
                            Fan-in:
                          </span>
                          <CommonInput
                            variant="field"
                            name="fan-in-cap"
                            type="number"
                            min="1"
                            max="99"
                            value={fanInCapDraft}
                            onChange={e => setFanInCapDraft(e.target.value)}
                            className="grade-edit-item__input"
                            disabled={rlSettingUpdateLoading}
                          />
                        </div>
                        {gradeFanOutRows.map(row => (
                          <div key={row.grade} className="grade-edit-item">
                            <span className="grade-edit-item__grade">
                              Fan-Out ({row.label}):
                            </span>
                            <CommonInput
                              variant="field"
                              name={`fan-out-cap-${row.grade}`}
                              type="number"
                              min="1"
                              max="99"
                              value={
                                fanOutCapDrafts[String(row.grade)] ??
                                String(DEFAULT_FAN_OUT_CAPS[String(row.grade)])
                              }
                              onChange={e =>
                                handleFanOutCapDraftChange(
                                  row.grade,
                                  e.target.value
                                )
                              }
                              className="grade-edit-item__input"
                              disabled={rlSettingUpdateLoading}
                            />
                          </div>
                        ))}
                        <div className="grade-edit-item">
                          <span className="grade-edit-item__grade">
                            Fan-out (over-all):
                          </span>
                          <CommonInput
                            variant="field"
                            name="overall-fan-out-cap"
                            type="number"
                            min="1"
                            max="999"
                            value={overallFanOutCapDraft}
                            onChange={e =>
                              setOverallFanOutCapDraft(e.target.value)
                            }
                            className="grade-edit-item__input"
                            disabled={rlSettingUpdateLoading}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Color Coding Settings Section */}
          {!isExecutiveSponsorLead && (
            <div className="settings-section bg-white p-6 rounded-lg shadow">
              <div className="settings-section__header">
                <div className="flex items-center justify-between w-full">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <Palette className="text-purple-600" size={20} />
                      <h2 className="fs-18 fw-600 mb-0">
                        Color Coding Thresholds
                      </h2>
                    </div>
                    <p className="text-sm text-gray-600">
                      Configure color coding thresholds for various metrics.
                      These thresholds determine how values are color-coded
                      throughout the system.
                    </p>
                  </div>
                  {!isEditingColorCoding ? (
                    <Button
                      variant="outline-primary"
                      onClick={handleEditColorCoding}
                      className="settings-edit-btn"
                    >
                      <Icon
                        name="pencilPrimary"
                        style={{ width: "0.875rem", height: "0.875rem" }}
                      />
                      Edit
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="primary"
                        onClick={handleSaveColorCoding}
                        className="settings-save-btn"
                        loading={colorCodingUpdateLoading}
                        disabled={!hasColorCodingChanges}
                      >
                        Save
                      </Button>
                      <Button
                        variant="gray"
                        onClick={handleCancelColorCoding}
                        className="settings-cancel-btn"
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              <div className="settings-section__content">
                {colorCodingLoading && !reduxColorCodingThresholds ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  </div>
                ) : (
                  <div className="color-coding-settings">
                    {/* Utilization Percentage Section */}
                    <div className="border border-gray-200 rounded-lg p-6 mb-4">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                            <span>Utilization Percentage</span>
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">
                            Define thresholds for executive sponsor capacity
                            utilization
                          </p>
                        </div>
                      </div>
                      <div className="space-y-6">
                        {/* Explanation */}
                        <div className="flex items-start space-x-2 p-4 bg-blue-50 rounded-lg">
                          <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm text-blue-800 font-medium mb-1">
                              How to set ranges:
                            </p>
                            <p className="text-sm text-blue-800">
                              • <strong>Green Zone:</strong> 0% to your chosen
                              upper limit (e.g., 0-100%)
                              <br />• <strong>Yellow Zone:</strong> Starts where
                              green ends to your chosen upper limit (e.g.,
                              100-120%)
                              <br />• <strong>Red Zone:</strong> Anything above
                              yellow&#39;s upper limit (e.g., 120%+) -
                              automatically calculated
                            </p>
                          </div>
                        </div>

                        {/* Range Input Section */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                          {/* Green Zone - Upper Limit */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              🟢 Green Zone Upper Limit
                            </label>
                            <div className="flex items-center space-x-3">
                              <CommonInput
                                variant="field"
                                name="green-threshold"
                                type="number"
                                step="1"
                                min="0"
                                max="200"
                                value={Math.round(
                                  colorCodingThresholds.utilization_percentage
                                    ?.greenThreshold || 0
                                ).toString()}
                                onChange={e =>
                                  handleColorCodingThresholdChange(
                                    "utilization_percentage",
                                    "greenThreshold",
                                    e.target.value
                                  )
                                }
                                className="flex-1"
                                disabled={!isEditingColorCoding}
                              />
                              <span className="text-gray-600">%</span>
                              <div className="w-8 h-8 rounded bg-green-500"></div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Range:{" "}
                              <strong>
                                0% to{" "}
                                {Math.round(
                                  colorCodingThresholds.utilization_percentage
                                    ?.greenThreshold || 0
                                )}
                                %
                              </strong>
                            </p>
                          </div>

                          {/* Yellow Zone - Upper Limit */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              🟡 Yellow Zone Upper Limit
                            </label>
                            <div className="flex items-center space-x-3">
                              <CommonInput
                                variant="field"
                                name="yellow-threshold"
                                type="number"
                                step="1"
                                min="0"
                                max="200"
                                value={Math.round(
                                  colorCodingThresholds.utilization_percentage
                                    ?.yellowThreshold || 0
                                ).toString()}
                                onChange={e =>
                                  handleColorCodingThresholdChange(
                                    "utilization_percentage",
                                    "yellowThreshold",
                                    e.target.value
                                  )
                                }
                                className="flex-1"
                                disabled={!isEditingColorCoding}
                              />
                              <span className="text-gray-600">%</span>
                              <div className="w-8 h-8 rounded bg-yellow-500"></div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Range:{" "}
                              <strong>
                                {Math.round(
                                  colorCodingThresholds.utilization_percentage
                                    ?.greenThreshold || 0
                                )}
                                % to{" "}
                                {Math.round(
                                  colorCodingThresholds.utilization_percentage
                                    ?.yellowThreshold || 0
                                )}
                                %
                              </strong>
                            </p>
                          </div>

                          {/* Red Zone Display (Not Editable) */}
                          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center justify-between">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  🔴 Red Zone (Automatically Calculated)
                                </label>
                                <p className="text-xs text-gray-500">
                                  Range:{" "}
                                  <strong>
                                    Above{" "}
                                    {Math.round(
                                      colorCodingThresholds
                                        .utilization_percentage
                                        ?.yellowThreshold || 0
                                    )}
                                    %
                                  </strong>{" "}
                                  - This is just a preview, not editable
                                </p>
                              </div>
                              <div className="w-8 h-8 rounded bg-red-500"></div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Visual Preview */}
                      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">
                          Color Preview
                        </h4>
                        <div className="grid grid-cols-3 gap-4">
                          {/* Green Example */}
                          <div className="text-center">
                            <div className="inline-flex items-center space-x-2 px-4 py-2 rounded bg-white border border-gray-200">
                              <span className="text-lg font-bold text-green-600">
                                {Math.round(
                                  (colorCodingThresholds.utilization_percentage
                                    ?.greenThreshold || 100) / 2
                                )}
                                %
                              </span>
                            </div>
                            <p className="text-xs text-gray-600 mt-2">
                              ≤{" "}
                              {colorCodingThresholds.utilization_percentage
                                ?.greenThreshold || 100}
                              % (Green)
                            </p>
                          </div>

                          {/* Yellow Example */}
                          <div className="text-center">
                            <div className="inline-flex items-center space-x-2 px-4 py-2 rounded bg-white border border-gray-200">
                              <span className="text-lg font-bold text-yellow-600">
                                {Math.round(
                                  ((colorCodingThresholds.utilization_percentage
                                    ?.greenThreshold || 100) +
                                    (colorCodingThresholds
                                      .utilization_percentage
                                      ?.yellowThreshold || 120)) /
                                    2
                                )}
                                %
                              </span>
                            </div>
                            <p className="text-xs text-gray-600 mt-2">
                              {colorCodingThresholds.utilization_percentage
                                ?.greenThreshold || 100}
                              % -{" "}
                              {colorCodingThresholds.utilization_percentage
                                ?.yellowThreshold || 120}
                              % (Yellow)
                            </p>
                          </div>

                          {/* Red Example */}
                          <div className="text-center">
                            <div className="inline-flex items-center space-x-2 px-4 py-2 rounded bg-white border border-gray-200">
                              <span className="text-lg font-bold text-red-600">
                                {Math.round(
                                  (colorCodingThresholds.utilization_percentage
                                    ?.yellowThreshold || 120) * 1.1
                                )}
                                %
                              </span>
                            </div>
                            <p className="text-xs text-gray-600 mt-2">
                              &gt;{" "}
                              {colorCodingThresholds.utilization_percentage
                                ?.yellowThreshold || 120}
                              % (Red)
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Administrative Tools */}
          {!isExecutiveSponsorLead && (
            <div className="settings-section bg-white p-6 rounded-lg shadow">
              <div className="settings-section__header">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="text-purple-600" size={20} />
                  <h2 className="fs-18 fw-600 mb-0">Administrative Tools</h2>
                </div>
              </div>
              <div className="settings-section__content">
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="mb-6">
                    <h3 className="text-base font-semibold text-gray-900">
                      Send Test Email (Resend)
                    </h3>
                    <p className="text-sm text-gray-600 mb-0">
                      Admins can send a test email to verify email delivery.
                    </p>
                  </div>
                  <div className="grid md:grid-cols-12 gap-3 items-start">
                    <div className="md:col-span-6">
                      <CommonInput
                        variant="field"
                        label="Recipient Email"
                        name="recipient-email"
                        type="email"
                        placeholder="name@example.com"
                        value={recipientEmail}
                        onChange={e => handleRecipientChange(e.target.value)}
                        error={recipientError || sendEmailError || ""}
                      />
                    </div>
                    <div className="md:col-span-6">
                      <DropdownSelector
                        label="Template"
                        selected={selectedEmailTemplate}
                        options={emailTemplateOptions}
                        onSelect={setSelectedEmailTemplate}
                        triggerStyle="field"
                        enableScroll
                        fullWidth
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-4">
                    <Button
                      variant="outline-primary"
                      onClick={handlePreviewTemplate}
                    >
                      Preview
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleSendTestEmail}
                      loading={sendEmailLoading}
                    >
                      Send Test Email
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <EmailTemplatePreviewModal
        isOpen={showTemplatePreview}
        toggle={() => setShowTemplatePreview(false)}
        template={selectedPreviewTemplate}
        emailTypeLabel={getTemplateLabel(selectedEmailTemplate)}
      />
    </div>
  );
};

export default Settings;
